import { useVerifyToken } from 'api/auth';
import { env } from 'app/Env/env';
import { DEFAULT_SSO_ID, QueryParamK, StorageK } from 'keys';
import React, { createContext, useContext, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { AuthSource, IResAuthorized, UnauthorizedReason } from 'types/api';

interface IAuthContextValue {
	authRes: IResAuthorized;
	setAuthRes: React.Dispatch<React.SetStateAction<IResAuthorized>>;
}

const AuthContext = createContext<IAuthContextValue | undefined>(undefined);

interface IAuthContextProviderProps {
	children: React.ReactNode;
	skipAuth?: boolean;
	initProjectId?: string | null;
	initTenantId?: string | null;
	initUnauthorizedReason?: UnauthorizedReason | null;
	initDomainVerificationTxt?: string | null;
	initAuthSource?: AuthSource | null;
}

const AuthContextProvider: React.FC<IAuthContextProviderProps> = ({
	children,
	skipAuth = false,
	initProjectId = null,
	initTenantId = null,
	initUnauthorizedReason = null,
	initDomainVerificationTxt = null,
	initAuthSource = null
}) => {
	const [authRes, setAuthRes] = useState<IResAuthorized>({
		done: skipAuth,
		projectId: initProjectId,
		verified: skipAuth,
		tenantId: initTenantId,
		unauthorizedReason: initUnauthorizedReason,
		domainVerificationTxt: initDomainVerificationTxt,
		authSource: initAuthSource
	});

	const value = useMemo(() => ({ authRes, setAuthRes }), [authRes]);

	return (
		<AuthContext.Provider value={value}>
			{/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
			{!skipAuth && <AuthContextInit />}
			{children}
		</AuthContext.Provider>
	);
};

const useAuthContext = (): IAuthContextValue => {
	const context = useContext(AuthContext);
	if (!context) throw new Error('AuthContextProvider is missing');
	return context;
};

const useAuthParams = () => {
	const [searchParams] = useSearchParams();
	const { pathname } = useLocation();
	const parts = pathname.split('/P');
	let pathProjectId = '';
	if (parts.length > 1) {
		// remove trailing /
		pathProjectId = `P${parts[1].split('/')[0]}`;
	}

	const qParamToken = searchParams.get(QueryParamK.Token) || '';
	const qParamTenantId = searchParams.get(QueryParamK.TenantId) || '';
	const qParamSSOId = searchParams.get(QueryParamK.SsoId) || '';
	const qParamCookieName = searchParams.get(QueryParamK.CookieName) || '';

	const storageProjectId = localStorage.getItem(StorageK.ProjectId) as string;
	const storageTenantId = localStorage.getItem(StorageK.TenantId) as string;
	const storageSSOId = localStorage.getItem(StorageK.SsoId) as string;
	const storageCookieName = localStorage.getItem(StorageK.CookieName) as string;

	const ssoId = qParamSSOId || env.ssoId || storageSSOId || '';

	return {
		token: qParamToken,
		projectId: pathProjectId || env.projectId || storageProjectId || '',
		tenantId: qParamTenantId || env.tenantId || storageTenantId || '',
		ssoId: ssoId === DEFAULT_SSO_ID ? '' : ssoId,
		cookieName: qParamCookieName || env.cookieName || storageCookieName || ''
	};
};

const AuthContextInit = () => {
	const { authRes, setAuthRes } = useAuthContext();

	const enabled = !authRes.done;

	const { token, projectId, tenantId, ssoId, cookieName } = useAuthParams();

	useVerifyToken(
		enabled,
		token,
		projectId,
		tenantId,
		ssoId,
		cookieName,
		setAuthRes
	);

	return null;
};

export { AuthContextProvider, useAuthContext };
